import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Shell, Card } from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { PasswordInput } from "../components/PasswordInput";
import { useAuth } from "../context/AuthContext";
import { apiError, http } from "../lib/api";

export default function TeamLogin() {
  const { login, logout } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [sent, setSent] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const data = await login(email, password);
      if (data.role !== "admin") {
        await logout();
        setError("Esta cuenta no pertenece al equipo de SISU. Utilice el acceso de familias.");
        return;
      }
      navigate("/admin");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell title="Acceso del equipo"
      subtitle="Área reservada al equipo de SISU: listado general de alumnado y paneles de administración."
      testid="team-login-page">
      <Card className="max-w-xl">
        <form onSubmit={submit} className="space-y-6">
          <div>
            <Label htmlFor="temail" className="text-lg">Correo del equipo</Label>
            <Input id="temail" data-testid="team-email-input" type="email" required value={email}
              onChange={(e) => setEmail(e.target.value)} className="mt-2 h-14 text-lg" />
          </div>
          <div>
            <Label htmlFor="tpass" className="text-lg">Contraseña</Label>
            <PasswordInput id="tpass" testid="team-password-input" required
              autoComplete="current-password" value={password}
              onChange={(e) => setPassword(e.target.value)} className="mt-2 h-14 text-lg" />
            <p className="mt-2 text-sm text-ink-700">
              Pulse el ojo para ver la contraseña mientras la escribe.
            </p>
          </div>
          {error && (
            <p data-testid="team-login-error" className="rounded-lg bg-red-50 p-4 text-base text-red-700">{error}</p>
          )}
          <Button data-testid="team-login-btn" disabled={busy} className="h-14 w-full text-lg">
            {busy ? "Accediendo..." : "Acceder al área del equipo"}
          </Button>
          <div className="rounded-xl bg-cream p-4 text-base text-ink-700">
            <p className="font-medium text-ink-900">¿Problemas para acceder?</p>
            <p className="mt-1">
              Introduzca el correo del equipo y su contraseña. Si no la recuerda, escriba el correo
              y pulse el botón siguiente: se enviará un enlace de restablecimiento a ese buzón, de
              modo que solo quien tenga acceso al correo puede cambiar la contraseña.
            </p>
            <p className="mt-2">
              Tras 5 intentos fallidos el acceso se bloquea 15 minutos por seguridad. El bloqueo se
              cuenta desde el primer fallo y caduca solo; los intentos posteriores no lo prolongan.
            </p>
            <Button type="button" data-testid="team-reset-btn" variant="outline"
              className="mt-3 h-12 text-base" disabled={busy}
              onClick={async () => {
                setError("");
                setSent("");
                if (!email.trim()) {
                  setError("Escriba primero el correo del equipo para recibir el enlace.");
                  return;
                }
                setBusy(true);
                try {
                  const { data } = await http.post("/auth/forgot-password", { email });
                  setSent(data.message);
                } catch (err) {
                  setError(apiError(err));
                } finally {
                  setBusy(false);
                }
              }}>
              Enviar enlace de restablecimiento al correo
            </Button>
            {sent && (
              <p data-testid="team-reset-msg" className="mt-3 rounded-lg bg-logo-light p-3 text-base text-logo-text">
                {sent}
              </p>
            )}
          </div>
        </form>
      </Card>
    </Shell>
  );
}
